// src/components/LAB/LABTrainingPeaksSummary.js
// Summary para actividades importadas desde TrainingPeaks
// Se muestra dentro del modal de actividad cuando source === 'trainingpeaks'
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const API = API_BASE_URL;

const LABTrainingPeaksSummary = ({ activityId }) => {
  console.log('[LABSummary] activityId:', activityId);
  const [act, setAct] = useState(null);

  useEffect(() => {
    if (!activityId) return;
    axios.get(`${API}/api/activities/${activityId}`)
      .then(r => setAct(r.data))
      .catch(() => {});
  }, [activityId]);

  if (!act) return null;
  // No mostrar si tiene archivo FIT - ya tiene ActivitySummary completo
  if (act.completed?.fileId || act.fitFile) return null;

  const safe = (v, decimals = 2) => {
    const n = parseFloat(v);
    return isNaN(n) ? '-' : n.toFixed(decimals);
  };

  const sportLabel = {
    correr: 'Running', ciclismo: 'Cycling', nadar: 'Swimming',
    fuerza: 'Strength', caminar: 'Walking', otro: 'Other', descanso: 'Rest'
  }[act.type] || act.type || '-';

  const hrAvg  = act.metrics?.ritmo_cardiaco?.avg || act.heartRateAvg;
  const hrMax  = act.metrics?.ritmo_cardiaco?.max || act.heartRateMax;
  const cadRaw = act.metrics?.cadencia?.avg || act.cadenceAvg;
  const cad    = (cadRaw && cadRaw !== '-' && !isNaN(Number(cadRaw))) ? cadRaw : null;
  const vel    = act.metrics?.velocidad?.avg || act.velocityAvg;
  const calcIF = () => {
    const stored = act.completed?.intensityFactor || act.intensityFactor;
    if (stored) return parseFloat(stored);
    const tss = parseFloat(act.completed?.tss || act.tss);
    const dur = act.completed?.duration; // "HH:MM:SS"
    if (!tss || !dur || typeof dur !== 'string') return null;
    const p = dur.split(':').map(Number);
    if (p.length !== 3 || p.some(isNaN)) return null;
    const hours = p[0] + p[1] / 60 + p[2] / 3600;
    if (hours <= 0) return null;
    return Math.sqrt(tss / (hours * 100));
  };
  const ifVal = calcIF();
  const toSwimPace = () => {
    // Pace de natacion desde duration (HH:MM:SS) y distance (km) de completed
    const dur = act.completed?.duration;
    const distKm = parseFloat(act.completed?.distance ?? act.completed?.distancia);
    if (!dur || typeof dur !== 'string' || !distKm || distKm <= 0) return null;
    const p = dur.split(':').map(Number);
    if (p.length !== 3 || p.some(isNaN)) return null;
    const secs = p[0] * 3600 + p[1] * 60 + p[2];
    const meters = distKm * 1000;
    const secPer100m = (secs / meters) * 100;
    // Sanity check: pace razonable entre 0:45 y 15:00 /100m
    if (secPer100m < 45 || secPer100m > 900) return null;
    const min = Math.floor(secPer100m / 60);
    const sec = Math.round(secPer100m % 60);
    return `${min}:${String(sec).padStart(2, '0')} /100m`;
  };
  const toPace = (kmh) => {
    const v = parseFloat(kmh);
    if (!v || isNaN(v) || v <= 0) return null;
    const minPerKm = 60 / v;
    let min = Math.floor(minPerKm);
    let sec = Math.round((minPerKm - min) * 60);
    if (sec === 60) { min += 1; sec = 0; }
    return `${min}:${String(sec).padStart(2, '0')} min/km`;
  };

  const metrics = [
    { label: '📅 Fecha',           value: act.date ? new Date(act.date).toLocaleDateString('es-CL') : '-' },
    { label: '🏃 Deporte',         value: sportLabel },
    { label: '⏱ Duración',         value: act.completed?.duration || '-' },
    { label: '📏 Distancia',        value: act.completed?.distance ? `${act.completed.distance} km` : '-' },
    { label: '⚡ TSS',              value: act.tss ? safe(act.tss) : '-' },
    { label: '💓 FC Promedio',      value: hrAvg ? `${Math.round(Number(hrAvg))} bpm` : '-' },
    { label: '💓 FC Máxima',        value: hrMax ? `${Math.round(Number(hrMax))} bpm` : '-' },
    ...(act.type === 'ciclismo'
      ? [{ label: '🚴 Velocidad media', value: vel ? `${safe(vel, 1)} km/h` : '-' }]
      : act.type === 'nadar'
      ? [{ label: '🏊 Ritmo medio',     value: toSwimPace() || '-' }]
      : [{ label: '🚀 Ritmo medio',     value: toPace(vel) || (act.completed?.avgPace ? `${act.completed.avgPace} min/km` : '-') }]),
    { label: '🦵 Cadencia',         value: cad ? `${Math.round(Number(cad))} rpm` : '-' },
    { label: '📊 IF',               value: ifVal ? safe(ifVal, 3) : '-' },
    { label: '🔥 Calorías',         value: act.completed?.calories ? `${act.completed.calories} kcal` : '-' },
    { label: '⛰️ Desnivel +',       value: act.completed?.totalAscent ? `${act.completed.totalAscent} m` : '-' },
    { label: '⛰️ Desnivel -',       value: act.completed?.totalDescent ? `${act.completed.totalDescent} m` : '-' },
    { label: '🫀 TE',               value: act.completed?.te ? safe(act.completed.te, 1) : '-' },
    { label: '⚡ TEA',              value: act.completed?.tea ? safe(act.completed.tea, 1) : '-' },
    { label: '⚙️ Potencia media',   value: act.completed?.avgPower ? `${Math.round(act.completed.avgPower)} W` : '-' },
    { label: '⚙️ Potencia máx',     value: act.completed?.maxPower ? `${Math.round(act.completed.maxPower)} W` : '-' },
    { label: '😤 RPE',              value: act.rpe || '-' },
    { label: '😊 Feeling',          value: act.feeling || '-' },
  ];

  const zones = act.hrZones;
  const totalZoneMin = zones ? Object.values(zones).reduce((a, b) => a + (b || 0), 0) : 0;

  return (
    <div style={{ marginTop: 16, padding: '14px 16px', background: '#f8fafc', borderRadius: 10, border: '0.5px solid #e5e7eb' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
        📊 Resumen
      </div>

      {/* Métricas en grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginBottom: 12 }}>
        {metrics.map((m, i) => m.value && (
          <div key={i} style={{ background: '#fff', border: '0.5px solid #e5e7eb', borderRadius: 8, padding: '8px 12px' }}>
            <div style={{ fontSize: 10, color: '#9ca3af' }}>{m.label}</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginTop: 2 }}>{m.value}</div>
          </div>
        ))}
      </div>

      {/* Zonas HR */}
      {zones && totalZoneMin > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Distribución Zonas HR</div>
          {['z1','z2','z3','z4','z5'].map((z, i) => {
            const min = zones[z] || 0;
            const pct = totalZoneMin > 0 ? Math.round((min / totalZoneMin) * 100) : 0;
            const colors = ['#10b981','#059669','#f59e0b','#ef4444','#dc2626'];
            return min > 0 ? (
              <div key={z} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 10, color: '#6b7280', width: 20 }}>Z{i+1}</span>
                <div style={{ flex: 1, background: '#f0f0f0', borderRadius: 99, height: 8, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: colors[i], borderRadius: 99 }} />
                </div>
                <span style={{ fontSize: 10, color: '#374151', width: 35, textAlign: 'right' }}>{pct}%</span>
                <span style={{ fontSize: 10, color: '#9ca3af', width: 50 }}>{Math.round(min)} min</span>
              </div>
            ) : null;
          })}
        </div>
      )}

      {/* Comentarios */}
      {act.coachComments && (
        <div style={{ marginBottom: 8, padding: '8px 12px', background: '#eff6ff', borderRadius: 8, border: '0.5px solid #bfdbfe' }}>
          <div style={{ fontSize: 10, color: '#1d4ed8', fontWeight: 600, marginBottom: 4 }}>💬 Comentarios del coach</div>
          <div style={{ fontSize: 12, color: '#1e3a5f', lineHeight: 1.5 }}>{act.coachComments}</div>
        </div>
      )}
      {act.athleteComments && (
        <div style={{ padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, border: '0.5px solid #bbf7d0' }}>
          <div style={{ fontSize: 10, color: '#166534', fontWeight: 600, marginBottom: 4 }}>💬 Comentarios del atleta</div>
          <div style={{ fontSize: 12, color: '#166534', lineHeight: 1.5 }}>{act.athleteComments}</div>
        </div>
      )}
    </div>
  );
};

export default LABTrainingPeaksSummary;
